import { ArrowRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import type { FunnelCarrier } from '@/models/funnelSession'
import { CurrentInsuranceAlert } from './CurrentInsuranceAlert'

/**
 * Left column in the results phase: the business context, estimate title,
 * supporting copy and the primary/secondary CTAs. The result cards themselves
 * live in the right column (ResultsPanel) so the CTAs sit alongside them.
 */
export function ResultsSidebar({
  carrier,
  currentInsurance,
  onApply,
  onTalkToExpert,
}: {
  carrier: FunnelCarrier | null
  currentInsurance?: { insurer: string; expirationDate: string | null }
  onApply: () => void
  onTalkToExpert: () => void
}) {
  const driverCount = carrier?.drivers ?? 0
  const summary = [
    carrier?.dotNumber && `DOT ${carrier.dotNumber}`,
    driverCount > 0 && `${driverCount} driver${driverCount === 1 ? '' : 's'}`,
    carrier?.state,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    // Desktop: follow the scroll (the left cell stretches to the taller results
    // column, so the sidebar has room to stick). top-20 clears the sticky nav.
    <div className="flex flex-col gap-5 lg:sticky lg:top-20">
      <div className="flex flex-col gap-1">
        <span className="eyebrow self-start">Estimating for</span>
        <span className="text-xl font-bold">{carrier?.name || 'Your business'}</span>
        {summary && <span className="text-muted-foreground text-sm">{summary}</span>}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-foreground text-3xl leading-tight font-bold md:text-4xl">
          Your auto liability estimates
        </h3>
        <p className="text-muted-foreground text-sm">
          These Cai estimates are based on your FMCSA profile. Complete a short application for accurate
          quotes across Auto Liability, Physical Damage, Cargo, and more.
        </p>
      </div>

      {/* Urgency nudge, right above the CTA — renewal is where a free quote lands.
          Only shown when there's a current policy on file, as in production. */}
      {currentInsurance?.expirationDate && (
        <CurrentInsuranceAlert
          insurer={currentInsurance.insurer}
          expirationDate={currentInsurance.expirationDate}
        />
      )}

      <div className="flex flex-col gap-3">
        <Button className="w-full" onClick={onApply}>
          Get quotes for free
          <ArrowRight className="size-4" />
        </Button>
        <Button variant="outline" className="w-full" onClick={onTalkToExpert}>
          Talk to an expert
        </Button>
        <p className="text-muted-foreground text-xs">5 minute application, with no requirement to purchase</p>
      </div>
    </div>
  )
}
